/**
 * CircleChat — Circle group chat.
 * Route: /circles/:circleSlug/chat
 *
 * Polling-based chat (5 s), NOT real-time.
 * Members only. Admin can delete messages and mute users.
 * 8 preset emoji reactions.
 */

import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  ArrowLeft, XCircle, MessageCircle, Smile, Trash2,
  VolumeX, Loader, Send, ChevronLeft,
} from 'react-feather';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useAuthGate } from '../hooks/useAuthGate';
import { useCircleId } from '../hooks/useCircleId';
import MonoAvatar from '../components/MonoAvatar';
import {
  getCircleDetail,
  getCircleMessages,
  sendCircleMessage,
  deleteCircleMessage,
  addMessageReaction,
  removeMessageReaction,
  muteMember,
} from '../services/circleService';
import type { Circle, CircleMember, CircleMessage } from '../types/circles';
import { formatTimestamp } from '../utils/dateUtils';
import '../styles/modern.css';

const PRESET_EMOJIS = ['\u{1F44D}', '\u{2764}\u{FE0F}', '\u{1F602}', '\u{1F62E}', '\u{1F622}', '\u{1F64F}', '\u{1F525}', '\u{1F389}'] as const;
const POLL_INTERVAL = 5000;
const MAX_MESSAGE_LENGTH = 250;
const PAGE_SIZE = 50;

export default function CircleChat() {
  const { circleSlug } = useParams<{ circleSlug: string }>();
  const { circleId, resolving } = useCircleId(circleSlug);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isAuthenticated, redirectToLogin } = useAuthGate();

  // ─── State ────────────────────────────────────────────────────────────
  const [circle, setCircle] = useState<Circle | null>(null);
  const [membership, setMembership] = useState<CircleMember | null>(null);
  const [messages, setMessages] = useState<CircleMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [emojiPickerFor, setEmojiPickerFor] = useState<string | null>(null);
  const [showOlderBtn, setShowOlderBtn] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const latestTimestampRef = useRef<string | null>(null);

  const isAdmin = membership?.role === 'admin';
  const isMember = membership?.status === 'active';
  const uid = user?.uid || user?.userId;

  // ─── Auth gate ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated && !loading) {
      redirectToLogin();
    }
  }, [isAuthenticated, loading, redirectToLogin]);

  // ─── Load circle info ────────────────────────────────────────────────
  useEffect(() => {
    if (!circleId || !isAuthenticated) return;

    (async () => {
      try {
        const data = await getCircleDetail(circleId);
        setCircle(data.circle);
        setMembership(data.membership);
        if (data.membership?.status !== 'active') {
          setError('You must be a member to view the chat.');
        }
      } catch (err: any) {
        setError(err?.message || 'Circle not found');
      }
    })();
  }, [circleId, isAuthenticated]);

  // ─── Fetch messages helper ────────────────────────────────────────────
  const fetchMessages = useCallback(
    async (opts?: { before?: number }) => {
      if (!circleId) return;
      try {
        const res = await getCircleMessages(circleId, { limit: PAGE_SIZE, before: opts?.before });
        return res;
      } catch {
        return null;
      }
    },
    [circleId],
  );

  // ─── Initial load ────────────────────────────────────────────────────
  useEffect(() => {
    if (!circleId || !isMember) return;

    let mounted = true;
    (async () => {
      setLoading(true);
      const res = await fetchMessages();
      if (res && mounted) {
        const sorted = (res.messages || []).sort((a, b) => {
          const aT = a.createdAt ? new Date(a.createdAt as any).getTime() : 0;
          const bT = b.createdAt ? new Date(b.createdAt as any).getTime() : 0;
          return aT - bT;
        });
        setMessages(sorted);
        // getCircleMessagesEurW returns no hasMore — infer it from a full page
        setShowOlderBtn(sorted.length === PAGE_SIZE);
        if (sorted.length > 0) {
          latestTimestampRef.current = (sorted[sorted.length - 1] as any).createdAt;
        }
        setLoading(false);
        setTimeout(() => scrollToBottom(), 100);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [circleId, isMember, fetchMessages]);

  // ─── Polling ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!circleId || !isMember) return;

    pollRef.current = setInterval(async () => {
      const res = await fetchMessages();
      if (res?.messages) {
        const sorted = (res.messages || []).sort((a, b) => {
          const aT = a.createdAt ? new Date(a.createdAt as any).getTime() : 0;
          const bT = b.createdAt ? new Date(b.createdAt as any).getTime() : 0;
          return aT - bT;
        });
        setMessages((prev) => {
          const ids = new Set(prev.map((m) => m.id));
          const newOnes = sorted.filter((m) => !ids.has(m.id));
          if (newOnes.length === 0) {
            const updated = prev.map((p) => {
              const fresh = sorted.find((s) => s.id === p.id);
              return fresh || p;
            });
            return updated;
          }
          const merged = [...prev, ...newOnes];
          return merged;
        });

        const isAtBottom = chatContainerRef.current
          ? chatContainerRef.current.scrollHeight - chatContainerRef.current.scrollTop - chatContainerRef.current.clientHeight < 150
          : true;
        if (isAtBottom) {
          setTimeout(() => scrollToBottom(), 50);
        }
      }
    }, POLL_INTERVAL);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [circleId, isMember, fetchMessages]);

  // ─── Scroll helper ────────────────────────────────────────────────────
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // ─── Load older ────────────────────────────────────────────────────────
  const loadOlder = async () => {
    if (messages.length === 0) return;
    const oldest = messages[0];
    if (!oldest.createdAt) return;
    setLoadingOlder(true);
    // getCircleMessagesEurW expects `before` as epoch millis (Timestamp.fromMillis)
    const res = await fetchMessages({ before: new Date(oldest.createdAt).getTime() });
    if (res?.messages) {
      const sorted = (res.messages || []).sort((a, b) => {
        const aT = a.createdAt ? new Date(a.createdAt as any).getTime() : 0;
        const bT = b.createdAt ? new Date(b.createdAt as any).getTime() : 0;
        return aT - bT;
      });
      setMessages((prev) => {
        const ids = new Set(prev.map((m) => m.id));
        const newOnes = sorted.filter((m) => !ids.has(m.id));
        return [...newOnes, ...prev];
      });
      setShowOlderBtn(sorted.length === PAGE_SIZE);
    }
    setLoadingOlder(false);
  };

  // ─── Send message ────────────────────────────────────────────────────
  const handleSend = async () => {
    if (!circleId || !text.trim() || sending) return;
    setSending(true);
    try {
      await sendCircleMessage(circleId, text.trim());
      setText('');
      const res = await fetchMessages();
      if (res?.messages) {
        const sorted = (res.messages || []).sort((a, b) => {
          const aT = a.createdAt ? new Date(a.createdAt as any).getTime() : 0;
          const bT = b.createdAt ? new Date(b.createdAt as any).getTime() : 0;
          return aT - bT;
        });
        setMessages(sorted);
      }
      setTimeout(() => scrollToBottom(), 100);
    } catch (err: any) {
      alert(err?.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // ─── Reactions ────────────────────────────────────────────────────────
  const toggleReaction = async (messageId: string, emoji: string) => {
    if (!circleId) return;
    setEmojiPickerFor(null);

    const msg = messages.find((m) => m.id === messageId);
    const existing = msg?.reactions?.[emoji];
    const alreadyReacted = existing?.includes(uid || '');

    try {
      if (alreadyReacted) {
        await removeMessageReaction(circleId, messageId, emoji);
      } else {
        await addMessageReaction(circleId, messageId, emoji);
      }

      setMessages((prev) =>
        prev.map((m) => {
          if (m.id !== messageId) return m;
          const reactions = { ...(m.reactions || {}) };
          const list = [...(reactions[emoji] || [])];
          if (alreadyReacted) {
            const idx = list.indexOf(uid || '');
            if (idx !== -1) list.splice(idx, 1);
            if (list.length === 0) delete reactions[emoji];
            else reactions[emoji] = list;
          } else {
            list.push(uid || '');
            reactions[emoji] = list;
          }
          return { ...m, reactions };
        }),
      );
    } catch {
      /* swallow – poll will fix */
    }
  };

  // ─── Admin: delete message ────────────────────────────────────────────
  const handleDeleteMessage = async (messageId: string) => {
    if (!circleId || !confirm('Delete this message?')) return;
    try {
      await deleteCircleMessage(circleId, messageId);
      setMessages((prev) => prev.filter((m) => m.id !== messageId));
    } catch (err: any) {
      alert(err?.message || 'Failed to delete message');
    }
  };

  // ─── Admin: mute user ─────────────────────────────────────────────────
  const handleMuteUser = async (userId: string) => {
    if (!circleId || !confirm('Mute this user?')) return;
    try {
      await muteMember(circleId, userId);
      alert('User muted');
    } catch (err: any) {
      alert(err?.message || 'Failed to mute user');
    }
  };

  // ─── Render ─────────────────────────────────────────────────────────────

  if (!isAuthenticated) return null;

  return (
    <div className="page-shell" style={{ display: 'flex', flexDirection: 'column', padding: 0 }}>
      <div style={{ maxWidth: 780, width: '100%', margin: '0 auto', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>

        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '14px 24px', borderBottom: '1px solid rgba(255,255,255,0.06)',
          flexShrink: 0,
        }}>
          <button
            onClick={() => navigate(`/circles/${circleSlug}`, { state: { circleId } })}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 999, padding: '7px 12px', color: '#fff', fontSize: 13,
              fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            <ChevronLeft size={14} />
          </button>
          <div>
            <h2 style={{ fontSize: 16, fontWeight: 700, color: '#fff', margin: 0 }}>
              {circle?.name || 'Chat'}
            </h2>
            {circle && (
              <span style={{ fontSize: 12, color: 'rgba(220,200,240,0.5)' }}>
                {circle.memberCount} member{circle.memberCount !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div style={{ textAlign: 'center', padding: '3rem 2rem' }}>
            <XCircle size={32} style={{ color: 'rgba(220,200,240,0.5)', marginBottom: 12 }} />
            <p style={{ color: 'rgba(220,200,240,0.7)' }}>{error}</p>
            <button
              className="primary-button"
              onClick={() => navigate(`/circles/${circleSlug}`, { state: { circleId } })}
              style={{ marginTop: 12 }}
            >
              back to circle
            </button>
          </div>
        )}

        {/* Loading */}
        {loading && !error && (
          <div style={{ padding: '3rem 24px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div className="skel-line wide pulse" style={{ height: 14 }} />
            <div className="skel-line medium pulse" style={{ height: 14 }} />
            <div className="skel-line narrow pulse" style={{ height: 14 }} />
          </div>
        )}

        {/* Messages */}
        {!loading && !error && isMember && (
          <>
            <div
              ref={chatContainerRef}
              style={{
                flex: 1, overflowY: 'auto', padding: '16px 24px',
                display: 'flex', flexDirection: 'column', gap: 8,
              }}
            >
              {showOlderBtn && (
                <div style={{ textAlign: 'center', padding: '8px 0' }}>
                  <button
                    onClick={loadOlder}
                    disabled={loadingOlder}
                    style={{
                      background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 8, padding: '6px 14px', color: 'rgba(220,200,240,0.7)',
                      fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
                    }}
                  >
                    {loadingOlder ? 'loading...' : 'load older messages'}
                  </button>
                </div>
              )}

              {messages.length === 0 && (
                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  justifyContent: 'center', padding: '4rem 1rem',
                  color: 'rgba(220,200,240,0.4)', gap: 10,
                }}>
                  <MessageCircle size={32} />
                  <p>No messages yet — start the conversation!</p>
                </div>
              )}

              {messages.map((msg) => {
                const isOwn = msg.senderId === uid;
                return (
                  <div
                    key={msg.id}
                    style={{
                      maxWidth: '80%', alignSelf: isOwn ? 'flex-end' : 'flex-start',
                      background: isOwn ? 'rgba(139,92,246,0.18)' : 'rgba(255,255,255,0.04)',
                      border: `1px solid ${isOwn ? 'rgba(139,92,246,0.2)' : 'rgba(255,255,255,0.06)'}`,
                      borderRadius: isOwn ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                      padding: '10px 14px',
                    }}
                  >
                    {!isOwn && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                        <MonoAvatar name={msg.senderName || 'U'} size={20} />
                        <span
                          style={{ fontSize: 12, fontWeight: 600, color: '#c9b3e6', cursor: 'pointer' }}
                          onClick={() => navigate(`/user/${msg.senderId}`)}
                        >
                          {msg.senderName || 'User'}
                        </span>
                        {msg.senderRole === 'admin' && (
                          <span style={{
                            fontSize: 10, padding: '1px 5px', borderRadius: 4,
                            background: 'rgba(139,92,246,0.25)', color: '#c9b3e6', fontWeight: 600,
                          }}>
                            Admin
                          </span>
                        )}
                      </div>
                    )}

                    <div style={{ fontSize: 14, color: '#EDEDED', lineHeight: 1.45, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {msg.text}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 5 }}>
                      <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>
                        {msg.createdAt ? formatTimestamp(msg.createdAt) : ''}
                      </span>

                      <button
                        onClick={() => setEmojiPickerFor(emojiPickerFor === msg.id ? null : msg.id)}
                        title="React"
                        style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.35)', cursor: 'pointer', padding: 0 }}
                      >
                        <Smile size={14} />
                      </button>

                      {isAdmin && !isOwn && (
                        <div style={{ display: 'flex', gap: 5 }}>
                          <button
                            title="Delete"
                            onClick={() => handleDeleteMessage(msg.id)}
                            style={{ background: 'none', border: 'none', color: 'rgba(255,107,107,0.5)', cursor: 'pointer', padding: 2 }}
                          >
                            <Trash2 size={14} />
                          </button>
                          <button
                            title="Mute user"
                            onClick={() => handleMuteUser(msg.senderId)}
                            style={{ background: 'none', border: 'none', color: 'rgba(255,107,107,0.5)', cursor: 'pointer', padding: 2 }}
                          >
                            <VolumeX size={14} />
                          </button>
                        </div>
                      )}
                    </div>

                    {emojiPickerFor === msg.id && (
                      <div style={{ display: 'flex', gap: 4, padding: '6px 0', flexWrap: 'wrap' }}>
                        {PRESET_EMOJIS.map((emoji) => (
                          <button
                            key={emoji}
                            onClick={() => toggleReaction(msg.id, emoji)}
                            style={{
                              background: 'none', border: 'none', fontSize: 18, cursor: 'pointer',
                              padding: '3px 5px', borderRadius: 6,
                            }}
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    )}

                    {msg.reactions && Object.keys(msg.reactions).length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                        {Object.entries(msg.reactions).map(([emoji, users]) => (
                          <button
                            key={emoji}
                            onClick={() => toggleReaction(msg.id, emoji)}
                            style={{
                              fontSize: 13, padding: '3px 8px', borderRadius: 999,
                              background: users.includes(uid || '') ? 'rgba(139,92,246,0.25)' : 'rgba(255,255,255,0.06)',
                              border: `1px solid ${users.includes(uid || '') ? 'rgba(139,92,246,0.4)' : 'rgba(255,255,255,0.08)'}`,
                              color: '#EDEDED', cursor: 'pointer', fontFamily: 'inherit',
                            }}
                          >
                            {emoji} {users.length}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div style={{
              display: 'flex', alignItems: 'flex-end', gap: 8,
              padding: '12px 24px', borderTop: '1px solid rgba(255,255,255,0.06)',
              flexShrink: 0, position: 'relative',
            }}>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value.slice(0, MAX_MESSAGE_LENGTH))}
                onKeyDown={handleKeyDown}
                placeholder="Type a message..."
                rows={1}
                disabled={sending}
                style={{
                  flex: 1, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 12, padding: '10px 14px', color: '#EDEDED', fontSize: 14,
                  resize: 'none', minHeight: 42, maxHeight: 120, fontFamily: 'inherit', outline: 'none',
                }}
              />
              <span style={{
                position: 'absolute', bottom: 16, right: 72, fontSize: 10,
                color: 'rgba(255,255,255,0.25)',
              }}>
                {text.length}/{MAX_MESSAGE_LENGTH}
              </span>
              <button
                className="primary-button"
                onClick={handleSend}
                disabled={!text.trim() || sending}
                style={{ padding: '10px 14px', borderRadius: 12, flexShrink: 0 }}
              >
                {sending ? <Loader size={16} className="spin" /> : <Send size={16} />}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
